import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Route, Switch } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import Queue from "./pages/Queue";
import History from "./pages/History";
import Import from "./pages/Import";
import Dashboard from "./pages/Dashboard";
import DashboardOperacao from "./pages/DashboardOperacao";
import OlViabilidade from "./pages/OlViabilidade";
import Users from "./pages/Users";
import Productivity from "./pages/Productivity";
import Products from "./pages/Products";
import CadastroBasicoProduto from "./pages/CadastroBasicoProduto";
import SomenteConsultaProdutos from "./pages/SomenteConsultaProdutos";
import Addresses from "./pages/Addresses";
import Emitentes from "./pages/Emitentes";
import Destinatarios from "./pages/Destinatarios";
import CadastroEtiquetas from "./pages/CadastroEtiquetas";
import Transportadoras from "./pages/Transportadoras";
import Contacts from "./pages/Contacts";
import AuditLog from "./pages/AuditLog";
import DigitalCertificate from "./pages/DigitalCertificate";
import ConsultaNFAgendada from "./pages/ConsultaNFAgendada";
import InvoiceHistory from "./pages/InvoiceHistory";
import InvoiceIssuer from "./pages/InvoiceIssuer";
import InventarioCego from "./pages/InventarioCego";
import QuoteConfig from "./pages/QuoteConfig";
import OperationNature from "./pages/OperationNature";
import FiscalReplication from "./pages/FiscalReplication";
import FechamentosBox from "./pages/FechamentosBox";
import FaturasOperacao from "./pages/FaturasOperacao";
import PriceTables from "./pages/PriceTables";
import Quotes from "./pages/Quotes";
import PaymentTerms from "./pages/PaymentTerms";
import ICMS from "./pages/ICMS";
import IPI from "./pages/IPI";
import CBS_IBS from "./pages/CBS_IBS";
import PIS_COFINS from "./pages/PIS_COFINS";
import FCP from "./pages/FCP";
import ISS from "./pages/ISS";
import MVA_ST from "./pages/MVA_ST";
import ReviewQueue from "./pages/ReviewQueue";
import BankAccounts from "./pages/BankAccounts";
import AccountsReceivable from "./pages/AccountsReceivable";
import CreditosDestinatarios from "./pages/CreditosDestinatarios";
import BaixaGuiadaComprovantes from "./pages/BaixaGuiadaComprovantes";
import SomenteConsultaContasReceber from "./pages/SomenteConsultaContasReceber";
import BankReceipts from "./pages/BankReceipts";
import BankReturnSettlements from "./pages/BankReturnSettlements";
import BaixasFrancesinha from "./pages/BaixasFrancesinha";
import StockMovement from "./pages/StockMovement";
import BaixaDanoExtravio from "./pages/BaixaDanoExtravio";
import AddressMovements from "./pages/AddressMovements";
import OrderSearch from "./pages/OrderSearch";
import SomenteConsultaPedidos from "./pages/SomenteConsultaPedidos";
import OpenOrders from "./pages/OpenOrders";
import DatabaseView from "./pages/DatabaseView";
import TaskManager from "./pages/TaskManager";
import AgendamentoEntradas from "./pages/AgendamentoEntradas";
import Recebimentos from "./pages/Recebimentos";
import CadastroPO from "./pages/CadastroPO";
import TemplateEtiquetas from "./pages/TemplateEtiquetas";
import ImprimirEtiquetas from "./pages/ImprimirEtiquetas";
import TratamentoEtiquetaVolumeFull from "./pages/TratamentoEtiquetaVolumeFull";
import CobrancaHistorico from "./pages/CobrancaHistorico";
import CommissionsConfig from "./pages/CommissionsConfig";
import ComissaoAcaoVenda from "./pages/ComissaoAcaoVenda";
import ComissoesOficiais from "./pages/ComissoesOficiais";
import MecomSed from "./pages/MecomSed";
import ProcessamentoEtiquetasExternas from "./pages/ProcessamentoEtiquetasExternas";
import Armazenamento from "./pages/Armazenamento";
import SearchBox from "./pages/SearchBox";
import Expedicao from "./pages/Expedicao";
import RecordOrders from "./pages/RecordOrders";
import UploadVideosMecom from "./pages/UploadVideosMecom";
import ImportarPedidos from "./pages/ImportarPedidos";
import QuoteXml from "./pages/QuoteXml";
import AccountsPayable from "./pages/AccountsPayable";
import SomenteConsultaContasPagar from "./pages/SomenteConsultaContasPagar";
import BankPayments from "./pages/BankPayments";
import SalesBox from "./pages/SalesBox";
import NoAccess from "./pages/NoAccess";
import Home from "./pages/Home";
import Financeiro from "./pages/Financeiro";
import { Sidebar } from "./components/Sidebar";
import { SystemStatusHeader } from "./components/SystemStatusHeader";
import Login from "./pages/Login";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { useGoogleAuth } from "./hooks/useGoogleAuth";
import { usePagePermissions } from "./hooks/usePagePermissions";
import Resolve from "./pages/Resolve";
import ResolveConfig from "./pages/ResolveConfig";
import CargosESalarios from "./pages/CargosESalarios";
import DepartamentoPessoal from "./pages/DepartamentoPessoal";
import LembretesGestao from "./pages/LembretesGestao";
import Processamento from "./pages/Processamento";
import RegistroPonto from "./pages/RegistroPonto";
import ControlePonto from "./pages/ControlePonto";
import Integracoes from "./pages/Integracoes";
import CartaCorrecao from "./pages/CartaCorrecao";
import ImportacaoBox from "./pages/ImportacaoBox";
import ImportacaoMondayResolve from "./pages/ImportacaoMondayResolve";
import TratativaInconformidade from "./pages/TratativaInconformidade";
import ImportacaoMondayTratativaInconformidade from "./pages/ImportacaoMondayTratativaInconformidade";
import PortalCliente from "./pages/PortalCliente";
import CatalogoPublico from "./pages/CatalogoPublico";
import AssinaturaPublica from "./pages/AssinaturaPublica";

/**
 * PageRoute: verifica permissão antes de renderizar a página.
 * - admin: acesso irrestrito
 * - cliente: verifica hasAccess(path)
 * - lider/operador: acesso liberado (controle feito pelo Sidebar)
 */
function PageRoute({ path, component: Component }: { path: string; component: React.ComponentType }) {
  const { user } = useGoogleAuth();
  const { hasAccess, loading } = usePagePermissions();

  if (loading) return null;

  // A rota raiz (/) é sempre acessível para todos os usuários autenticados
  // Rotas de callback OAuth são tratadas com a permissão da rota pai
  const permissionPath = path.startsWith("/integracoes/ml/") ? "/integracoes" : path;
  if (user?.role === "cliente" && permissionPath !== "/" && !hasAccess(permissionPath)) {
    return <NoAccess />;
  }

  return <Component />;
}

function Router() {
  return (
    <Switch>
      <Route path="/login" component={Login} />
      <Route path="/portal-cliente" component={PortalCliente} />
      <Route path="/catalogo-publico/:emitente" component={CatalogoPublico} />
      <Route path="/financeiro/:pedidoId/:nfeNumero" component={Financeiro} />
      <Route path="/assinatura-publica/:token" component={AssinaturaPublica} />
      <Route path="/rh-ponto" component={RegistroPonto} />
      <Route>
        <ProtectedRoute>
          <div className="flex min-h-screen flex-col">
            <SystemStatusHeader />
            <div className="flex flex-1">
              <Sidebar />
              <main className="flex-1 lg:ml-64">
                <Switch>
                  <Route path={"/"}>{() => <PageRoute path="/" component={Home} />}</Route>
                  <Route path={"/queue"}>{() => <PageRoute path="/queue" component={Queue} />}</Route>
                  <Route path={"/history"}>{() => <PageRoute path="/history" component={History} />}</Route>
                  <Route path={"/import"}>{() => <PageRoute path="/import" component={Import} />}</Route>
                  <Route path={"/dashboard"}>{() => <PageRoute path="/dashboard" component={Dashboard} />}</Route>
                  <Route path={"/dashboard-operacao"}>{() => <PageRoute path="/dashboard-operacao" component={DashboardOperacao} />}</Route>
                  <Route path={"/ol-viabilidade"}>{() => <PageRoute path="/ol-viabilidade" component={OlViabilidade} />}</Route>
                  <Route path={"/users"}>{() => <PageRoute path="/users" component={Users} />}</Route>
                  <Route path={"/productivity"}>{() => <PageRoute path="/productivity" component={Productivity} />}</Route>
                  <Route path={"/products"}>{() => <PageRoute path="/products" component={Products} />}</Route>
                  <Route path={"/cadastro-basico-produto"}>{() => <PageRoute path="/cadastro-basico-produto" component={CadastroBasicoProduto} />}</Route>
                  <Route path={"/somente-consulta-produtos"}>{() => <PageRoute path="/somente-consulta-produtos" component={SomenteConsultaProdutos} />}</Route>
                  <Route path={"/addresses"}>{() => <PageRoute path="/addresses" component={Addresses} />}</Route>
                  <Route path={"/emitentes"}>{() => <PageRoute path="/emitentes" component={Emitentes} />}</Route>
                  <Route path={"/recipients"}>{() => <PageRoute path="/recipients" component={Destinatarios} />}</Route>
                  <Route path={"/cadastro-etiquetas"}>{() => <PageRoute path="/cadastro-etiquetas" component={CadastroEtiquetas} />}</Route>
                  <Route path={"/carriers"}>{() => <PageRoute path="/carriers" component={Transportadoras} />}</Route>
                  <Route path={"/contacts"}>{() => <PageRoute path="/contacts" component={Contacts} />}</Route>
                  <Route path={"/audit-log"}>{() => <PageRoute path="/audit-log" component={AuditLog} />}</Route>
                  <Route path={"/digital-certificate"}>{() => <PageRoute path="/digital-certificate" component={DigitalCertificate} />}</Route>
                  <Route path={"/scheduled-invoice-query"}>{() => <PageRoute path="/scheduled-invoice-query" component={ConsultaNFAgendada} />}</Route>
                  <Route path={"/invoice-history"}>{() => <PageRoute path="/invoice-history" component={InvoiceHistory} />}</Route>
                  <Route path={"/invoice-issuer"}>{() => <PageRoute path="/invoice-issuer" component={InvoiceIssuer} />}</Route>
                  <Route path={"/inventario-cego"}>{() => <PageRoute path="/inventario-cego" component={InventarioCego} />}</Route>
                  <Route path={"/quote-config"}>{() => <PageRoute path="/quote-config" component={QuoteConfig} />}</Route>
                  <Route path={"/operation-nature"}>{() => <PageRoute path="/operation-nature" component={OperationNature} />}</Route>
                  <Route path={"/fiscal-replication"}>{() => <PageRoute path="/fiscal-replication" component={FiscalReplication} />}</Route>
                  <Route path={"/fechamentos-box"}>{() => <PageRoute path="/fechamentos-box" component={FechamentosBox} />}</Route>
                  <Route path={"/faturas-operacao"}>{() => <PageRoute path="/faturas-operacao" component={FaturasOperacao} />}</Route>
                  <Route path={"/tax-icms"}>{() => <PageRoute path="/tax-icms" component={ICMS} />}</Route>
                  <Route path={"/price-tables"}>{() => <PageRoute path="/price-tables" component={PriceTables} />}</Route>
                  <Route path={"/quotes"}>{() => <PageRoute path="/quotes" component={Quotes} />}</Route>
                  <Route path={"/review-queue"}>{() => <PageRoute path="/review-queue" component={ReviewQueue} />}</Route>
                  <Route path={"/bank-accounts"}>{() => <PageRoute path="/bank-accounts" component={BankAccounts} />}</Route>
                  <Route path={"/accounts-receivable"}>{() => <PageRoute path="/accounts-receivable" component={AccountsReceivable} />}</Route>
                  <Route path={"/creditos-destinatarios"}>{() => <PageRoute path="/creditos-destinatarios" component={CreditosDestinatarios} />}</Route>
                  <Route path={"/baixa-guiada-comprovantes"}>{() => <PageRoute path="/baixa-guiada-comprovantes" component={BaixaGuiadaComprovantes} />}</Route>
                  <Route path={"/somente-consulta-contas-receber"}>{() => <PageRoute path="/somente-consulta-contas-receber" component={SomenteConsultaContasReceber} />}</Route>
                  <Route path={"/bank-receipts"}>{() => <PageRoute path="/bank-receipts" component={BankReceipts} />}</Route>
                  <Route path={"/bank-return-settlements"}>{() => <PageRoute path="/bank-return-settlements" component={BankReturnSettlements} />}</Route>
                  <Route path={"/francesinha"}>{() => <PageRoute path="/francesinha" component={BaixasFrancesinha} />}</Route>
                  <Route path={"/stock-movement"}>{() => <PageRoute path="/stock-movement" component={StockMovement} />}</Route>
                  <Route path={"/baixa-dano-extravio"}>{() => <PageRoute path="/baixa-dano-extravio" component={BaixaDanoExtravio} />}</Route>
                  <Route path={"/address-movements"}>{() => <PageRoute path="/address-movements" component={AddressMovements} />}</Route>
                  <Route path={"/order-search"}>{() => <PageRoute path="/order-search" component={OrderSearch} />}</Route>
                  <Route path={"/somente-consulta-pedidos"}>{() => <PageRoute path="/somente-consulta-pedidos" component={SomenteConsultaPedidos} />}</Route>
                  <Route path={"/open-orders"}>{() => <PageRoute path="/open-orders" component={OpenOrders} />}</Route>
                  <Route path={"/payment-terms"}>{() => <PageRoute path="/payment-terms" component={PaymentTerms} />}</Route>
                  <Route path={"/tax-ipi"}>{() => <PageRoute path="/tax-ipi" component={IPI} />}</Route>
                  <Route path={"/tax-cbs"}>{() => <PageRoute path="/tax-cbs" component={CBS_IBS} />}</Route>
                  <Route path={"/tax-pis-cofins"}>{() => <PageRoute path="/tax-pis-cofins" component={PIS_COFINS} />}</Route>
                  <Route path={"/tax-fcp"}>{() => <PageRoute path="/tax-fcp" component={FCP} />}</Route>
                  <Route path={"/tax-mva-st"}>{() => <PageRoute path="/tax-mva-st" component={MVA_ST} />}</Route>
                  <Route path={"/tax-iss"}>{() => <PageRoute path="/tax-iss" component={ISS} />}</Route>
                  <Route path={"/admin/database-view"}>{() => <PageRoute path="/admin/database-view" component={DatabaseView} />}</Route>
                  <Route path={"/task-manager"}>{() => <PageRoute path="/task-manager" component={TaskManager} />}</Route>
                  <Route path={"/entry-schedule"}>{() => <PageRoute path="/entry-schedule" component={AgendamentoEntradas} />}</Route>
                  <Route path={"/logistics-receipts"}>{() => <PageRoute path="/logistics-receipts" component={Recebimentos} />}</Route>
                  <Route path={"/workbenches"}>{() => <PageRoute path="/workbenches" component={CadastroPO} />}</Route>
                  <Route path={"/label-templates"}>{() => <PageRoute path="/label-templates" component={TemplateEtiquetas} />}</Route>
                  <Route path={"/print-labels"}>{() => <PageRoute path="/print-labels" component={ImprimirEtiquetas} />}</Route>
                  <Route path={"/tratamento-etiqueta-volume-full"}>{() => <PageRoute path="/tratamento-etiqueta-volume-full" component={TratamentoEtiquetaVolumeFull} />}</Route>
                  <Route path={"/billing-history"}>{() => <PageRoute path="/billing-history" component={CobrancaHistorico} />}</Route>
                  <Route path={"/comissoes-config"}>{() => <PageRoute path="/comissoes-config" component={CommissionsConfig} />}</Route>
                  <Route path={"/comissao-acao-venda"}>{() => <PageRoute path="/comissao-acao-venda" component={ComissaoAcaoVenda} />}</Route>
                  <Route path={"/comissoes-oficiais"}>{() => <PageRoute path="/comissoes-oficiais" component={ComissoesOficiais} />}</Route>
                  <Route path={"/sales-box"}>{() => <PageRoute path="/sales-box" component={SalesBox} />}</Route>
                  <Route path={"/mecom-sed"}>{() => <PageRoute path="/mecom-sed" component={MecomSed} />}</Route>
                  <Route path={"/processamento-etiquetas-externas"}>{() => <PageRoute path="/processamento-etiquetas-externas" component={ProcessamentoEtiquetasExternas} />}</Route>
                  <Route path={"/armazenamento"}>{() => <PageRoute path="/armazenamento" component={Armazenamento} />}</Route>
                  <Route path={"/search-box"}>{() => <PageRoute path="/search-box" component={SearchBox} />}</Route>
                  <Route path={"/expedicao"}>{() => <PageRoute path="/expedicao" component={Expedicao} />}</Route>
                  <Route path={"/record-orders"}>{() => <PageRoute path="/record-orders" component={RecordOrders} />}</Route>
                  <Route path={"/upload-videos-mecom"}>{() => <PageRoute path="/upload-videos-mecom" component={UploadVideosMecom} />}</Route>
                  <Route path={"/importar-pedidos"}>{() => <PageRoute path="/importar-pedidos" component={ImportarPedidos} />}</Route>
                  <Route path={"/importacao-box"}>{() => <PageRoute path="/importacao-box" component={ImportacaoBox} />}</Route>
                  <Route path={"/importacao-monday-resolve"}>{() => <PageRoute path="/importacao-monday-resolve" component={ImportacaoMondayResolve} />}</Route>
                  <Route path={"/importacao-monday-tratativa-inconformidade"}>{() => <PageRoute path="/importacao-monday-tratativa-inconformidade" component={ImportacaoMondayTratativaInconformidade} />}</Route>
                  <Route path={"/quote-xml"}>{() => <PageRoute path="/quote-xml" component={QuoteXml} />}</Route>
                  <Route path={"/accounts-payable"}>{() => <PageRoute path="/accounts-payable" component={AccountsPayable} />}</Route>
                  <Route path={"/somente-consulta-contas-pagar"}>{() => <PageRoute path="/somente-consulta-contas-pagar" component={SomenteConsultaContasPagar} />}</Route>
                  <Route path={"/bank-payments"}>{() => <PageRoute path="/bank-payments" component={BankPayments} />}</Route>
                  <Route path={"/resolve"}>{() => <PageRoute path="/resolve" component={Resolve} />}</Route>
                  <Route path={"/tratativa-inconformidade"}>{() => <PageRoute path="/tratativa-inconformidade" component={TratativaInconformidade} />}</Route>
                  <Route path={"/resolve-config"}>{() => <PageRoute path="/resolve-config" component={ResolveConfig} />}</Route>
                  <Route path={"/positions"}>{() => <PageRoute path="/positions" component={CargosESalarios} />}</Route>
                  <Route path={"/rh-departamento"}>{() => <PageRoute path="/rh-departamento" component={DepartamentoPessoal} />}</Route>
                  <Route path={"/lembretes-gestao"}>{() => <PageRoute path="/lembretes-gestao" component={LembretesGestao} />}</Route>
                  <Route path={"/processamento"}>{() => <PageRoute path="/processamento" component={Processamento} />}</Route>
                  <Route path={"/controle-ponto"}>{() => <PageRoute path="/controle-ponto" component={ControlePonto} />}</Route>
                  <Route path={"/integracoes"}>{() => <PageRoute path="/integracoes" component={Integracoes} />}</Route>
                  <Route path={"/integracoes/ml/callback"}>{() => <PageRoute path="/integracoes/ml/callback" component={Integracoes} />}</Route>
                  <Route path={"/correction-letter"}>{() => <PageRoute path="/correction-letter" component={CartaCorrecao} />}</Route>
                  <Route path={"/404"} component={NotFound} />
                  <Route component={NotFound} />
                </Switch>
              </main>
            </div>
          </div>
        </ProtectedRoute>
      </Route>
    </Switch>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider
        defaultTheme="light"
        // switchable
      >
        <TooltipProvider>
          <Toaster />
          <Router />
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
